import { Box, Button, Typography } from "@mui/material";
import { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { ArrowForwardIos, ArrowBackIos } from "@mui/icons-material";
import { useStyles } from "./style";
import { NewLearningRequestStep } from "utils/types";
import { SurveyType } from "utils/graphql/Global";
import { useTranslation } from "react-i18next";
import { Loader } from "components/loader/loader";
import { SURVEY_DATA, SUBMIT_Additional_DETAILS } from "utils/queries";
import * as SurveyTypes from "utils/graphql/SurveyData";
import { useQuery, useMutation } from "@apollo/client/react";
import { SurveyContainer } from "../../../question-types/survey-container/survey-container";
import portalSettingsManager from "utils/portalSettingsManager";
import { mapToResponse, validateAllQuestions } from "../../../shared/validation";
import { useAppDispatch } from "store";
import { logger } from "utils/logger";
import {
  NEW_LEARNING_ACTION_TYPE,
  NEW_LEARNING_ALERT_TYPE,
  focusFirstError,
  showAction,
} from "../../../shared/newLearningHelper";

interface NlrAdditionalDetailsProps {
  onNext: (currentStepParam: NewLearningRequestStep) => void;
  onPrevious: () => void;
  onSubmitSuccess?: () => void;
  setDialogTitle?: (title: string) => void;
  isMobile?: boolean;
}

export const NlrAdditionalDetails: FC<NlrAdditionalDetailsProps> = ({
  onNext,
  onPrevious,
  onSubmitSuccess,
  setDialogTitle,
  isMobile = false,
}) => {
  const classes = useStyles({ isMobile });
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const questionRefs: MutableRefObject<Record<string, HTMLDivElement | null>> = useRef({});
  const [questions, setQuestions] = useState<SurveyTypes.SurveyData_survey_questions[]>([]);
  const [survey, setSurvey] = useState<SurveyTypes.SurveyData_survey | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, any>>({});

  const handleAnswerChange = useCallback((questionId: string, value: any) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setValidationErrors((prev) => ({
      ...prev,
      [questionId]: {},
    }));
  }, []);

  const {
    loading,
    data,
    error: queryError,
  } = useQuery<SurveyTypes.SurveyData>(SURVEY_DATA, {
    variables: {
      surveyType: SurveyType.NLR_ADDITIONAL_DETAILS,
    },
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (queryError) {
      logger.error("Failed to load NLR additional details data", { error: queryError });
      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
        type: NEW_LEARNING_ALERT_TYPE.ERROR,
        title: t("additional-details"),
        message: t("nlr_additional_details_load_error_alert_message"),
      });
      setQuestions([]);
    }
  }, [queryError, dispatch, t]);

  const [submitNlrDetails] = useMutation(SUBMIT_Additional_DETAILS);

  const handleSubmit = useCallback(async () => {
    const learnerAnswers = mapToResponse(answers);

    const submitNlrSurveyInput = {
      surveyId: survey?.surveyId,
      learnerAnswers,
    };

    logger.info("Submitting NLR additional details", {
      surveyId: survey?.surveyId,
      answerCount: learnerAnswers?.length ?? 0,
    });

    showAction(dispatch, NEW_LEARNING_ACTION_TYPE.LOADER_SHOW_AND_HIDE, {
      show: true,
      message: t("additional-details"),
    });

    try {
      await submitNlrDetails({
        variables: {
          input: submitNlrSurveyInput,
        },
      });
      logger.info("NLR additional details submitted successfully");
      onSubmitSuccess?.();
      onNext(NewLearningRequestStep.RequestSubmitted);
    } catch (error) {
      logger.error("Failed to submit NLR additional details", {
        surveyId: survey?.surveyId,
        error: error instanceof Error ? error.message : String(error),
      });

      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
        type: NEW_LEARNING_ALERT_TYPE.ERROR,
        title: t("additional-details"),
        message: t("nlr_additional_details_save_error"),
      });
    } finally {
      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.LOADER_SHOW_AND_HIDE, {
        show: false,
      });
    }
  }, [answers, survey?.surveyId, submitNlrDetails, onNext, onSubmitSuccess, dispatch, t]);

  const handleNext = useCallback(() => {
    if (!questions.length) return;

    const { isValid, errors } = validateAllQuestions(questions, answers, {
      skipMinCharValidation: true,
    });
    if (!isValid) {
      setValidationErrors(errors);
      // This helps keyboard and screen reader users immediately know where the problem is
      focusFirstError(errors, questionRefs.current);
      return;
    }

    handleSubmit();
  }, [questions, answers, handleSubmit]);

  useEffect(() => {
    if (data?.survey) {
      setSurvey(data.survey as SurveyTypes.SurveyData_survey);
      setQuestions(data.survey.questions as SurveyTypes.SurveyData_survey_questions[]);
      // Clear validation errors when survey loads
      setValidationErrors({});
    }
  }, [data]);

  useEffect(() => {
    setDialogTitle?.(t("additional-details"));
  }, [setDialogTitle, t]);

  return (
    <Box className={classes.container}>
      <Loader isDisplay={loading}></Loader>
      <Box className={classes.contentCard}>
        <Typography variant="h2" className={classes.cardTitle}>
          {t("additional-details")}
        </Typography>
        <Box className={classes.fieldsContainer}>
          {survey?.questions?.map((question, index) => {
            return (
              <div
                key={question.questionId}
                ref={(el) => {
                  questionRefs.current[question.questionId] = el;
                }}
              >
                <Box className={classes.questionItem}>
                  <SurveyContainer
                    question={question}
                    questionIndex={index}
                    answer={answers[question.questionId]}
                    onAnswerChange={handleAnswerChange}
                    validationErrors={validationErrors[question.questionId] || {}}
                    maxCharacters={
                      portalSettingsManager.surveyCharacterMinMaxThreshold
                        ?.surveyLearnerCommentMaxLength
                    }
                  />
                </Box>
              </div>
            );
          })}
        </Box>
        <Box className={classes.buttonGroup}>
          <Button
            id="nlr-additional-details-continue-btn"
            type="button"
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForwardIos />}
            className={classes.primaryButton}
            aria-label={t("continue")}
          >
            {t("submit-request")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
